<template>
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-start justify-center overflow-auto bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <div class="my-8 w-full max-w-4xl rounded-lg bg-base-100 shadow-2xl">
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-base-300 p-4">
          <div>
            <h2 class="text-lg font-semibold">State Inspector</h2>
            <p class="text-sm text-base-content/60">View and edit Pinia store state</p>
          </div>
          <button class="btn btn-ghost btn-sm" @click="emit('close')">
            <m-icon :icon="faTimes" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex h-[70vh] min-h-[400px]">
          <!-- Store List -->
          <div class="w-56 flex-shrink-0 overflow-auto border-r border-base-300 p-2">
            <div class="mb-2 text-xs font-semibold uppercase text-base-content/50">Stores</div>
            <button
              v-for="storeId in storeIds"
              :key="storeId"
              class="mb-1 flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm transition-colors"
              :class="selectedStore === storeId ? 'bg-primary text-primary-content' : 'hover:bg-base-200'"
              @click="selectStore(storeId)"
            >
              <span class="truncate">{{ storeId }}</span>
              <span
                v-if="getStoreProtectionLevel(storeId)"
                class="ml-1 flex-shrink-0 rounded px-1 text-[10px] font-bold uppercase"
                :class="getProtectionBadgeClass(storeId)"
                :title="getProtectionTooltip(storeId)"
              >
                {{ getStoreProtectionLevel(storeId) }}
              </span>
            </button>
          </div>

          <!-- State View -->
          <div class="flex flex-1 flex-col overflow-hidden">
            <!-- Toolbar -->
            <div class="flex items-center justify-between border-b border-base-300 bg-base-200/50 px-4 py-2">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium">{{ selectedStore || 'Select a store' }}</span>
                <span
                  v-if="selectedStore && selectedStoreProtectionLevel"
                  class="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
                  :class="getProtectionBadgeClass(selectedStore)"
                >
                  {{ selectedStoreProtectionLevel }} level
                </span>
              </div>
              <div class="flex gap-2">
                <button
                  v-if="selectedStore"
                  class="btn btn-ghost btn-xs"
                  :class="{ 'btn-active': viewMode === 'tree' }"
                  @click="viewMode = 'tree'"
                >
                  Tree
                </button>
                <button
                  v-if="selectedStore"
                  class="btn btn-ghost btn-xs"
                  :class="{ 'btn-active': viewMode === 'json' }"
                  @click="viewMode = 'json'"
                >
                  JSON
                </button>
              </div>
            </div>

            <!-- Protection Warning Banner -->
            <div
              v-if="selectedStore && selectedStoreProtectionLevel"
              class="border-b px-4 py-2 text-xs"
              :class="getProtectionBannerClass(selectedStore)"
            >
              <m-icon :icon="faShieldAlt" class="mr-1" />
              <strong>{{ selectedStoreProtectionLevel.toUpperCase() }}-level data:</strong>
              {{ getProtectionWarningText(selectedStore) }}
            </div>

            <!-- State Content -->
            <div v-if="selectedStore && (selectedStoreState || selectedStoreGetters)" class="flex-1 overflow-auto p-4">
              <!-- Tree View -->
              <div v-if="viewMode === 'tree'">
                <!-- State Section -->
                <div v-if="selectedStoreState" class="mb-4">
                  <div class="mb-2 flex items-center gap-2">
                    <span class="text-xs font-semibold uppercase text-base-content/60">State</span>
                    <span class="rounded bg-base-200 px-1.5 py-0.5 text-[10px] text-base-content/50">
                      {{ Object.keys(selectedStoreState).length }} properties
                    </span>
                  </div>
                  <div class="rounded border border-base-300 bg-base-100 p-3">
                    <StateTree
                      :data="selectedStoreState"
                      :path="[]"
                      :protection-level="selectedStoreProtectionLevel"
                      @update="handleStateUpdate"
                    />
                  </div>
                </div>

                <!-- Getters Section -->
                <div v-if="selectedStoreGetters">
                  <div class="mb-2 flex items-center gap-2">
                    <span class="text-xs font-semibold uppercase text-secondary">Getters</span>
                    <span class="rounded bg-secondary/10 px-1.5 py-0.5 text-[10px] text-secondary">
                      {{ Object.keys(selectedStoreGetters).length }} computed
                    </span>
                    <span class="text-[10px] italic text-base-content/40">(read-only)</span>
                  </div>
                  <div class="rounded border border-secondary/30 bg-secondary/5 p-3">
                    <StateTree
                      :data="selectedStoreGetters"
                      :path="[]"
                      :protection-level="selectedStoreProtectionLevel"
                      :read-only="true"
                    />
                  </div>
                </div>
              </div>

              <!-- JSON View -->
              <div v-else-if="viewMode === 'json'" class="flex h-full flex-col">
                <textarea
                  v-model="jsonEditValue"
                  class="textarea textarea-bordered h-full w-full font-mono text-xs"
                  spellcheck="false"
                />
                <div class="mt-2 flex justify-end gap-2">
                  <button class="btn btn-ghost btn-sm" @click="resetJsonEdit">Reset</button>
                  <button class="btn btn-primary btn-sm" @click="applyJsonEdit">Apply JSON</button>
                </div>
              </div>
            </div>

            <div v-else class="flex flex-1 items-center justify-center text-base-content/50">
              {{ selectedStore ? 'No state or getters available' : 'Select a store to inspect' }}
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between border-t border-base-300 bg-base-200/30 px-4 py-3">
          <div class="text-xs text-base-content/50">
            <m-icon :icon="faExclamationTriangle" class="mr-1 text-warning" />
            Changes are applied directly to the store state. Use with caution.
          </div>
          <button class="btn btn-ghost btn-sm" @click="emit('close')">Close</button>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialog -->
    <div
      v-if="confirmDialog"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60"
      @click.self="confirmDialog = null"
    >
      <div class="w-full max-w-md rounded-lg bg-base-100 p-6 shadow-2xl">
        <div class="mb-4 flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-full" :class="confirmDialog.iconBgClass">
            <m-icon :icon="faExclamationTriangle" class="text-lg" :class="confirmDialog.iconClass" />
          </div>
          <h3 class="text-lg font-semibold">{{ confirmDialog.title }}</h3>
        </div>
        <p class="mb-4 text-sm text-base-content/70">{{ confirmDialog.message }}</p>
        <div class="mb-4 rounded bg-base-200 p-3 font-mono text-xs">
          <div class="text-base-content/50">Path:</div>
          <div class="break-all">{{ confirmDialog.path }}</div>
        </div>
        <div class="flex justify-end gap-2">
          <button class="btn btn-ghost btn-sm" @click="confirmDialog = null">Cancel</button>
          <button class="btn btn-sm" :class="confirmDialog.confirmBtnClass" @click="executeConfirmedUpdate">
            I understand, apply change
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
  import { ref, computed, watch, defineAsyncComponent, toRefs } from 'vue';
  import { getActivePinia } from 'pinia';
  import { faTimes, faExclamationTriangle, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

  // Lazy load the StateTree component to avoid circular issues
  const StateTree = defineAsyncComponent(() => import('./StateTree.vue'));

  // Store protection levels - higher levels require confirmation before editing
  type ProtectionLevel = 'system' | 'user' | 'studio' | null;

  const STORE_PROTECTION_MAP: Record<string, ProtectionLevel> = {
    // System-level stores - most sensitive, affects entire application
    account: 'system',

    // User-level stores - affects user session/preferences
    adminlayout: 'user',

    // Studio-level stores - affects studio configuration
    features: 'studio',
    studioadminstudio: 'studio',
    packageSet: 'studio',
  };

  interface ConfirmDialogState {
    title: string;
    message: string;
    path: string;
    iconBgClass: string;
    iconClass: string;
    confirmBtnClass: string;
    pendingUpdate: { path: (string | number)[]; value: unknown } | null;
    pendingJsonUpdate: boolean;
  }

  const props = defineProps<{
    isOpen: boolean;
  }>();

  const { isOpen } = toRefs(props);

  const emit = defineEmits<{
    close: [];
  }>();

  const selectedStore = ref<string | null>(null);
  const viewMode = ref<'tree' | 'json'>('tree');
  const jsonEditValue = ref('');
  const confirmDialog = ref<ConfirmDialogState | null>(null);

  // Store IDs - refreshed when panel opens (Pinia's _s Map is not reactive)
  const storeIds = ref<string[]>([]);

  // Refresh the store list from Pinia's internal store map
  function refreshStoreIds(): void {
    const pinia = getActivePinia();
    if (!pinia) {
      storeIds.value = [];
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storeMap = (pinia as any)._s as Map<string, any>;
    storeIds.value = storeMap ? Array.from(storeMap.keys()).sort() : [];
  }

  // Refresh store list when modal opens
  watch(
    isOpen,
    (nowOpen) => {
      if (nowOpen) {
        refreshStoreIds();
      }
    },
    { immediate: true },
  );

  // Get the selected store instance
  const selectedStoreInstance = computed(() => {
    if (!selectedStore.value) return null;
    const pinia = getActivePinia();
    if (!pinia) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storeMap = (pinia as any)._s as Map<string, any>;
    return storeMap?.get(selectedStore.value) ?? null;
  });

  // Get the selected store's state
  const selectedStoreState = computed(() => {
    const store = selectedStoreInstance.value;
    if (!store) return null;
    return store.$state;
  });

  // Get the selected store's getters (computed properties)
  const selectedStoreGetters = computed(() => {
    const store = selectedStoreInstance.value;
    if (!store) return null;

    const stateKeys = new Set(Object.keys(store.$state || {}));
    const getters: Record<string, unknown> = {};

    // Iterate over the store's own properties to find getters
    // Getters are non-function values that aren't in $state and aren't internal ($, _)
    for (const key of Object.keys(store)) {
      // Skip internal Pinia properties
      if (key.startsWith('$') || key.startsWith('_')) continue;
      // Skip state properties
      if (stateKeys.has(key)) continue;

      try {
        const value = store[key];
        // Actions are functions - skip them
        if (typeof value === 'function') continue;

        // This is a getter - add it
        getters[key] = value;
      } catch (e) {
        // Some getters may throw if dependencies aren't ready
        getters[key] = `[Error: ${e instanceof Error ? e.message : String(e)}]`;
      }
    }

    return Object.keys(getters).length > 0 ? getters : null;
  });

  // Get protection level for the selected store
  const selectedStoreProtectionLevel = computed(() => {
    return selectedStore.value ? getStoreProtectionLevel(selectedStore.value) : null;
  });

  // Protection level helper functions
  function getStoreProtectionLevel(storeId: string): ProtectionLevel {
    return STORE_PROTECTION_MAP[storeId] ?? null;
  }

  function getProtectionBadgeClass(storeId: string): string {
    const level = getStoreProtectionLevel(storeId);
    switch (level) {
      case 'system':
        return 'bg-red-100 text-red-700';
      case 'user':
        return 'bg-amber-100 text-amber-700';
      case 'studio':
        return 'bg-purple-100 text-purple-700';
      default:
        return '';
    }
  }

  function getProtectionBannerClass(storeId: string): string {
    const level = getStoreProtectionLevel(storeId);
    switch (level) {
      case 'system':
        return 'bg-red-50 border-red-200 text-red-700';
      case 'user':
        return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'studio':
        return 'bg-purple-50 border-purple-200 text-purple-700';
      default:
        return '';
    }
  }

  function getProtectionTooltip(storeId: string): string {
    const level = getStoreProtectionLevel(storeId);
    switch (level) {
      case 'system':
        return 'System-level: Authentication & permissions. Changes may break the entire session.';
      case 'user':
        return 'User-level: User preferences & layout. Changes affect user experience.';
      case 'studio':
        return 'Studio-level: Studio configuration & features. Changes affect studio functionality.';
      default:
        return '';
    }
  }

  function getProtectionWarningText(storeId: string): string {
    const level = getStoreProtectionLevel(storeId);
    switch (level) {
      case 'system':
        return 'Modifying this store can break authentication, permissions, or the entire session. You may need to refresh the page to recover.';
      case 'user':
        return 'Modifying this store affects user preferences and layout settings. Changes may cause unexpected UI behavior.';
      case 'studio':
        return 'Modifying this store affects studio configuration. Changes may impact feature availability or studio-wide settings.';
      default:
        return '';
    }
  }

  function getConfirmDialogConfig(level: ProtectionLevel): Partial<ConfirmDialogState> {
    switch (level) {
      case 'system':
        return {
          title: 'Modify System-Level Data?',
          message:
            'You are about to modify system-level data (authentication, permissions). This could break your session and require a page refresh to recover. Are you sure you want to proceed?',
          iconBgClass: 'bg-red-100',
          iconClass: 'text-red-700',
          confirmBtnClass: 'btn-error',
        };
      case 'user':
        return {
          title: 'Modify User-Level Data?',
          message:
            'You are about to modify user-level data (preferences, layout). This could cause unexpected UI behavior. Are you sure you want to proceed?',
          iconBgClass: 'bg-amber-100',
          iconClass: 'text-amber-700',
          confirmBtnClass: 'btn-warning',
        };
      case 'studio':
        return {
          title: 'Modify Studio-Level Data?',
          message:
            'You are about to modify studio-level data (features, configuration). This could affect studio functionality. Are you sure you want to proceed?',
          iconBgClass: 'bg-purple-100',
          iconClass: 'text-purple-700',
          confirmBtnClass: 'btn-secondary',
        };
      default:
        return {};
    }
  }

  function selectStore(storeId: string) {
    selectedStore.value = storeId;
    viewMode.value = 'tree';
    updateJsonEditValue();
  }

  function updateJsonEditValue() {
    if (selectedStoreState.value) {
      try {
        jsonEditValue.value = JSON.stringify(selectedStoreState.value, null, 2);
      } catch (e) {
        jsonEditValue.value = `// Error serializing state: ${e}`;
      }
    }
  }

  function resetJsonEdit() {
    updateJsonEditValue();
  }

  function applyJsonEdit() {
    const protectionLevel = selectedStoreProtectionLevel.value;

    // If protected, show confirmation dialog first
    if (protectionLevel) {
      const config = getConfirmDialogConfig(protectionLevel);
      confirmDialog.value = {
        ...config,
        path: `${selectedStore.value} (full state)`,
        pendingUpdate: null,
        pendingJsonUpdate: true,
      } as ConfirmDialogState;
      return;
    }

    executeJsonUpdate();
  }

  function executeJsonUpdate() {
    const store = selectedStoreInstance.value;
    if (!store) return;

    try {
      const newState = JSON.parse(jsonEditValue.value);
      // Replace $state wholesale so keys removed from the JSON are also removed
      store.$patch((state: Record<string, unknown>) => {
        // Remove keys that are no longer in the edited JSON
        for (const key of Object.keys(state)) {
          if (!(key in newState)) {
            delete state[key];
          }
        }
        // Set / overwrite all keys present in the new JSON
        Object.keys(newState).forEach((key) => {
          state[key] = newState[key];
        });
      });
    } catch (e) {
      alert(`Invalid JSON: ${e}`);
    }
  }

  // Handle updates from the tree view
  function handleStateUpdate(path: (string | number)[], value: unknown) {
    const store = selectedStoreInstance.value;
    if (!store || path.length === 0) return;

    const protectionLevel = selectedStoreProtectionLevel.value;

    // If protected, show confirmation dialog first
    if (protectionLevel) {
      const config = getConfirmDialogConfig(protectionLevel);
      confirmDialog.value = {
        ...config,
        path: path.join(' → '),
        pendingUpdate: { path, value },
        pendingJsonUpdate: false,
      } as ConfirmDialogState;
      return;
    }

    executeUpdate(path, value);
  }

  function executeUpdate(path: (string | number)[], value: unknown) {
    const store = selectedStoreInstance.value;
    if (!store) return;

    // Use function form of $patch to navigate and set the value directly
    // This properly handles arrays and nested objects
    store.$patch((state: Record<string | number, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let current: any = state;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
    });
  }

  function executeConfirmedUpdate() {
    if (!confirmDialog.value) return;

    if (confirmDialog.value.pendingJsonUpdate) {
      executeJsonUpdate();
    } else if (confirmDialog.value.pendingUpdate) {
      executeUpdate(confirmDialog.value.pendingUpdate.path, confirmDialog.value.pendingUpdate.value);
    }

    confirmDialog.value = null;
  }

  // Update JSON when switching to JSON view
  watch(viewMode, (mode) => {
    if (mode === 'json') {
      updateJsonEditValue();
    }
  });
</script>
